import { useEffect, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "./App.css";

// Components
import Landing from "./components/Landing";
import Login from "./components/Login";
import Register from "./components/Register";
import Dashboard from "./components/Dashboard";
import InvoiceBuilder from "./components/InvoiceBuilder";
import InvoiceView from "./components/InvoiceView";
import Clients from "./components/Clients";
import Settings from "./components/Settings";
import PublicInvoice from "./components/PublicInvoice";
import Billing from "./components/Billing";
import Analytics from "./components/Analytics";
import Onboarding from "./components/Onboarding";
import Changelog from "./components/Changelog";
import AdminDashboard from "./components/AdminDashboard";
import LifetimeDeal from "./components/LifetimeDeal";
import GettingStarted from "./components/GettingStarted";
import Estimates from "./components/Estimates";
import EstimateBuilder from "./components/EstimateBuilder";
import PublicEstimate from "./components/PublicEstimate";
import RecurringInvoices from "./components/RecurringInvoices";
import Blog from "./components/Blog";
import BlogPost from "./components/BlogPost";
import BlogAdmin from "./components/BlogAdmin";
import HelpDocs from "./components/HelpDocs";
import IndustryPage from "./components/IndustryPage";
import Expenses from "./components/Expenses";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Fetch full user data including logo_url
    await checkAuth();
    return data;
  };

  const register = async (name, email, password, business_name) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, business_name });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
        <div className="text-xl font-bold">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect new users to onboarding (skip if already on onboarding page)
  if (user.onboarding_complete === false && window.location.pathname !== "/onboarding" && window.location.pathname !== "/invoices/new") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
        <div className="text-xl font-bold">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/invoices/new" element={<ProtectedRoute><InvoiceBuilder /></ProtectedRoute>} />
          <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceView /></ProtectedRoute>} />
          <Route path="/invoices/:id/edit" element={<ProtectedRoute><InvoiceBuilder /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/getting-started" element={<ProtectedRoute><GettingStarted /></ProtectedRoute>} />
          <Route path="/estimates" element={<ProtectedRoute><Estimates /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
          <Route path="/estimates/new" element={<ProtectedRoute><EstimateBuilder /></ProtectedRoute>} />
          <Route path="/estimates/:id/edit" element={<ProtectedRoute><EstimateBuilder /></ProtectedRoute>} />
          <Route path="/recurring" element={<ProtectedRoute><RecurringInvoices /></ProtectedRoute>} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/admin" element={<ProtectedRoute><BlogAdmin /></ProtectedRoute>} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/help" element={<HelpDocs />} />
          <Route path="/for/:industry" element={<IndustryPage />} />
          <Route path="/lifetime" element={<LifetimeDeal />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/invoice/:token" element={<PublicInvoice />} />
          <Route path="/estimate/:token" element={<PublicEstimate />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export { API };
export default App;
